import {
  OrderPriceAdjustmentLineDto,
  OrderPriceAdjustmentTotalsDto,
  PutOrderPriceAdjustmentLineDto,
} from "../../../../data/models/responses/OrderPriceAdjustmentDto";

// Pure helpers of the admin "Adjust prices" dialog (flag
// REACT_APP_ADMIN_PRICE_ADJUSTMENT_ENABLED). Display-side only: the totals
// here mirror the API's OrderTotalsRule (sub-total = sum of qty x effective
// price, VAT = sub-total x rate, total = both) so the dialog can show
// "Before -> After" while typing - the API's answer is the fact.

// The draft: one raw input string per adjustable line id. "" = no agreement
// (our price applies). Kept as text so the admin can type "4,50" or "4.5"
// without the field fighting back.
export type PriceDraft = Record<number, string>;

// Upper bound mirrored from the API policy (MaxUnitPrice).
export const MAX_UNIT_PRICE = 1_000_000;

export interface ParsedPrice {
  valid: boolean;
  // null = empty input = no agreement.
  value: number | null;
}

// "" -> no agreement; "4,50" / "4.5" / " 8 " -> the number; anything else
// (negative, NaN, > MAX, more than 2 decimals, letters) -> invalid. The
// dialog limits input to 2 decimals (NAV amounts are 2-decimal; the API
// allows 6 for future imports).
export function parsePriceInput(raw: string): ParsedPrice {
  const text = (raw ?? "").trim();
  if (text === "") return { valid: true, value: null };
  const normalized = text.replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return { valid: false, value: null };
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0 || value > MAX_UNIT_PRICE) {
    return { valid: false, value: null };
  }
  return { valid: true, value };
}

export function effectivePrice(
  ourPrice: number,
  agreedPrice: number | null | undefined,
): number {
  return agreedPrice === null || agreedPrice === undefined
    ? ourPrice
    : agreedPrice;
}

export interface TotalsLine {
  quantity: number;
  ourPrice: number;
  agreedPrice: number | null;
}

// Same formula as the API's OrderTotalsRule.Compute - not rounded here; the
// dialog rounds for display only.
export function computeTotals(
  lines: TotalsLine[],
  vatRate: number,
): OrderPriceAdjustmentTotalsDto {
  const subTotal = lines.reduce(
    (sum, line) => sum + line.quantity * effectivePrice(line.ourPrice, line.agreedPrice),
    0,
  );
  const vat = subTotal * (vatRate / 100);
  return { subTotal, vat, total: subTotal + vat };
}

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// The draft as the dialog opens: the stored agreed price of each adjustable
// line (as text, 2 decimals) or "" when there is none. The API allows 6
// decimals on a stored price (future imports); the 2-decimal text is only
// how the field shows it - evaluateDraft treats that exact text as
// "untouched" so the dialog never reports a change nobody made.
export function initialDraft(lines: OrderPriceAdjustmentLineDto[]): PriceDraft {
  const draft: PriceDraft = {};
  for (const line of lines) {
    if (!line.adjustable) continue;
    draft[line.lineId] =
      line.agreedPrice === null || line.agreedPrice === undefined
        ? ""
        : formatPriceInput(line.agreedPrice);
  }
  return draft;
}

export function formatPriceInput(value: number): string {
  return round2(value).toFixed(2);
}

export function samePrice(a: number | null, b: number | null): boolean {
  if (a === null || b === null) return a === b;
  return Math.abs(a - b) < 0.000001;
}

// The text initialDraft put in the field for this stored price - i.e. what
// the admin sees without having typed anything.
function isUntouchedText(raw: string, stored: number | null): boolean {
  return stored === null ? raw === "" : raw === formatPriceInput(stored);
}

export interface DraftEvaluation {
  // PUT lines: only the adjustable lines whose agreed price changed
  // (null = remove the agreement). An omitted line is unchanged.
  changes: PutOrderPriceAdjustmentLineDto[];
  invalidLineIds: number[];
  hasChange: boolean;
  isValid: boolean;
  // The lines with the draft applied, for the "After" totals.
  effectiveLines: TotalsLine[];
}

// Reads the draft against the loaded lines. Invalid input keeps the stored
// price in the "After" preview (so the totals never show garbage) and blocks
// saving; non-adjustable lines are never touched.
export function evaluateDraft(
  lines: OrderPriceAdjustmentLineDto[],
  draft: PriceDraft,
): DraftEvaluation {
  const changes: PutOrderPriceAdjustmentLineDto[] = [];
  const invalidLineIds: number[] = [];
  const effectiveLines: TotalsLine[] = [];
  for (const line of lines) {
    const stored = line.agreedPrice ?? null;
    if (!line.adjustable) {
      effectiveLines.push({
        quantity: line.quantity,
        ourPrice: line.ourPrice,
        agreedPrice: stored,
      });
      continue;
    }
    const raw = draft[line.lineId];
    // Missing from the draft, or exactly the pre-filled text (a stored
    // 4.123456 shows as "4.12"): the stored price stands, at full precision,
    // so the "After" preview equals "Before" and Save stays disabled.
    const parsed =
      raw === undefined || isUntouchedText(raw, stored)
        ? { valid: true, value: stored }
        : parsePriceInput(raw);
    if (!parsed.valid) {
      invalidLineIds.push(line.lineId);
      effectiveLines.push({
        quantity: line.quantity,
        ourPrice: line.ourPrice,
        agreedPrice: stored,
      });
      continue;
    }
    if (!samePrice(parsed.value, stored)) {
      changes.push({ lineId: line.lineId, agreedPrice: parsed.value });
    }
    effectiveLines.push({
      quantity: line.quantity,
      ourPrice: line.ourPrice,
      agreedPrice: parsed.value,
    });
  }
  return {
    changes,
    invalidLineIds,
    hasChange: changes.length > 0,
    isValid: invalidLineIds.length === 0,
    effectiveLines,
  };
}

// Which tooltip a locked line gets: Design (MUL) lines carry no price,
// Delivery lines are never sent to NAV as a line. Anything else locked by
// the API gets the generic text.
export function notAdjustableKey(productType: string): string {
  if (productType === "Design") return "orderPriceAdjustment.notAdjustableDesign";
  if (productType === "Delivery")
    return "orderPriceAdjustment.notAdjustableDelivery";
  return "orderPriceAdjustment.notAdjustableLine";
}

// The startup fee (X_100) also reaches NAV as startupPrice on the logo line
// (a live NAV lookup the adjustment cannot touch) - the dialog warns when an
// AdditionalFee line is adjustable so the owner verifies the invoice in NAV.
export function hasAdjustableAdditionalFee(
  lines: OrderPriceAdjustmentLineDto[],
): boolean {
  return lines.some(
    (line) =>
      line.adjustable &&
      (line.productType === "AdditionalFee" ||
        line.productType === "Additional Fee"),
  );
}
